import { Inject, Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { AhsTranslationService } from '@nx-ahs/base-translate';
import { Locale } from '@nx-ahs/base-translate';

import { ContactUsModel } from '../models/contact-us.model';
import { ConversationModel } from '../models/conversation.model';
import { MessageModel } from '../models/message.model';

import { locale as english } from '../i18n/contact-us.en';
import { locale as spanish } from '../i18n/contact-us.es';
import { locale as creole } from '../i18n/contact-us.cr';

@Injectable({
	providedIn: 'root'
})
export class SecureMessagingService {
	public unreadMessageCount = new EventEmitter<number>();

	public dataOriginId = 2; // override during implementation
	public conversationTypeId = 1; // override during implementation

	public englishText: Locale = english;
	public spanishText: Locale = spanish;
	public creoleText: Locale = creole;

	constructor(
		@Inject('environment') public environment: any,
		//public ahsTranslationService: AhsTranslationService,
		public httpClient: HttpClient
	) {}

	public changeConversationStatus(conversationId: number, status: string): Observable<any> {
		return this.httpClient.post<any>(
			this.environment.apiUrl +
				`api/Portal/ChangeConversationStatus?conversationId=${conversationId}&status=${status}`,
			null
		);
	}

	public getMessages(): Observable<ConversationModel[]> {
		return this.httpClient.get<ConversationModel[]>(this.environment.apiUrl + `api/Portal/GetContactUsMessages`);
	}

	public getConversation(conversationId: number): Observable<ConversationModel[]> {
		return this.httpClient.get<ConversationModel[]>(
			this.environment.apiUrl + `api/Contract/GetContactUsConversation?conversationId=${conversationId}`
		);
	}

	public saveMessage(model: ContactUsModel): Observable<any> {
		if (!model.dataOriginId) model.dataOriginId = this.dataOriginId;
		//model.conversationTypeId = this.conversationTypeId;
		return this.httpClient.post<any>(this.environment.apiUrl + 'api/Portal/SendContactUsMessage', model);
	}

	public async setMessageAsRead(
		messageList: MessageModel[],
		conversationId: number,
		isHealthTrackMode: boolean,
		userEntityNameId: number
	): Promise<any> {
		/*
		||  If data orgin is portal, mark messages from health track as read; otherwise mark portal messages as read
		*/
		const senderEntityNameIdToUpdate: number = isHealthTrackMode ? 28 : userEntityNameId;
		const dataOriginId: number = isHealthTrackMode ? 1 : 2;

		messageList.forEach((m) => {
			if (!m.readDate && m.senderEntityNameId == senderEntityNameIdToUpdate) m.readDate = new Date(); // will be replaced with server time on the next get, this is just to keep track of if it is read and maybe show date in the UI.
		});

		const result = await this.httpClient
			.post<any>(
				this.environment.apiUrl +
					`api/Portal/SetMessageAsRead?conversationId=${conversationId}&&dataOrigin=${dataOriginId}&&userEntityNameId=${userEntityNameId}`,
				null
			)
			.toPromise()
			.then();

		//Bug 49241 - Only get unread message count in portal mode
		if (!isHealthTrackMode) {
			this.getUnreadMessageCount();
		}

		return of(result);
	}

	public getUnreadMessageCount(): void {
		this.httpClient.get<number>(this.environment.apiUrl + 'api/Portal/GetUnredMessageCount').subscribe((data) => {
			this.unreadMessageCount.emit(data);
		});
	}

	public getAccounts(userId?: number, isHealthTrackMode: boolean = false): Observable<any[]> {
		if (!isHealthTrackMode) {
			return this.httpClient.post<any>(this.environment.apiUrl + `api/Portal/GetPortalEntities`, null);
		} else {
			return of([]);
		}
	}

	public getCommRefData(): Observable<any> {
		return this.httpClient.get<any>(this.environment.apiUrl + 'api/Contract/ClientCache?category=Comm');
	}
}

export class SubjectDescription {
	codename: string;
	translatedDescription: string;
}
